import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LavorisService from "../../services/LavorisService";
import { TableActions } from "../../components/others/TableActions/index";
import AddEmployee from "../others/addEmployee/index";
import EliminarModal from "../../components/others/EliminarModal";
import EditEmployeeModal from "../others/editEmployee";
import { useModal } from "../../hooks";
import ImportEmployeesModal from "../others/importEmployeesModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import { useAlert } from "../../hooks/useAlert";
import { getErrorMessage } from "../../utils";
import { Badge, BadgeProps } from "@mui/material";
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { PhoneNumber } from "../../utils/phoneNumber";
import employeesExcelReport from "./employeesExcelReport";
import InfoOffcanvas from '../../components/others/InfoOffCanvas/InfoOffCanvas';
import "./employee_header.scss";
import InfiniteTable from "../../components/others/InfiniteTableComponent/IntiniteTableComponent";

const Employees = () => {
  const LavorisInstance = LavorisService.getInstance();
  const chosenCompany = useSelector((state: RootState) => state.user.currentCompany || state.user.companies[0]);
  const { showAlert } = useAlert();
  const [data, setData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const hasSearchQuery = searchQuery && searchQuery.length > 0
  const [items, setItems] = useState({
    itemsPerPage: 20,
    page: 1,
  });
  const [excelLoading, setExcelLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams("");
  const { openModal, closeModal, isActive } = useModal();
  const controller = new AbortController();
  const signal = controller.signal;

  const getInfiniteEmployeesCall = async () => {
    setIsLoading(true)
    try {
      setHasMore(true);
      const employees = await LavorisInstance.CompanyServices.getEmployeesOfCurrentCompanyPagination(
        chosenCompany.id,
        signal,
        items.itemsPerPage,
        items.page,
        hasSearchQuery ? searchQuery : ""
      );

      if (employees.length === 0) {
        setHasMore(false);
        return;
      }

      const existingIds = new Set(data?.map(item => item.id));
      const uniqueEmployees = employees?.filter(employee => !existingIds.has(employee.id));

      setData(prevData => [...prevData, ...uniqueEmployees]); // Agrega nuevos empleados al estado existente
      setItems(prev => ({ ...prev, page: prev.page + 1 })); // Incrementa la página
    } catch (error) {
      console.log(error);
      setHasMore(false);
    } finally {
      setIsLoading(false)
    }
  };

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, [])


  useEffect(() => {
    getInfiniteEmployeesCall();
  }, []);

  const getEmployeesCallRefresh = async () => {
    setIsLoading(true);
    try {
      setHasMore(true);
      const employees = await LavorisInstance.CompanyServices.getEmployeesOfCurrentCompanyPagination(
        chosenCompany.id,
        signal,
        items.itemsPerPage,
        1,
        hasSearchQuery ? searchQuery : ""
      );

      if (employees.length === 0) {
        setHasMore(false);
        return;
      }
      setData([])
      setData(employees)
      setItems(prev => ({ ...prev, page: prev.page + 1 })); // Incrementa la página
    } catch (error) {
      console.log(error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const querySearch = e.target.value;
    setSearchQuery(querySearch);
  };

  const searchWitQuery = async () => {
    setIsLoading(true);

    try {

      const employees = await LavorisInstance.CompanyServices.getEmployeesOfCurrentCompanyPagination(
        chosenCompany.id,
        signal,
        items.itemsPerPage,
        1,
        searchQuery
      );

      if (employees?.length === 0) {
        showAlert("No se encontraron registros");
        return
      } else {
        setData([])
        setData(employees);
        setItems(prev => ({ ...prev, page: prev.page + 1 }));
        setHasMore(true);
      }

    } catch (error) {
      console.log(error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onSearch = async () => {
    try {
      if (hasSearchQuery) {
        await searchWitQuery()
      } else {
        await getEmployeesCallRefresh()
      }
    } catch (error) {
      console.log(error);
    }
  };

  const HandleDeletion = async () => {
    setIsLoading(true);
    try {
      let EmployeeID: any = searchParams.get("rowId");
      await LavorisInstance.CompanyServices.deleteEmployee(EmployeeID);
      closeModal("EliminateModal");
      await getEmployeesCallRefresh()
    } catch (err) {
      showAlert(getErrorMessage({ title: "No se pudo eliminar al Empleado", err }));
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));

  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: -3,
      top: 13,
      border: `2px solid ${theme.palette.background.paper}`,

    },
  }));

  const getAllEmployees = async () => {
    setExcelLoading(true)
    try {
      const employees = await LavorisInstance.CompanyServices.getAllEmployees();
      await employeesExcelReport(employees);
    } catch (err) {
      showAlert(getErrorMessage({ title: 'error al descargar excel', err }))
    } finally {
      setExcelLoading(false)
    }
  }

  const employeeColumns = [
    {
      key: 'name',
      title: 'Apellido, Nombre',
      className: 'col-name',
      expandable: false, // Permite expandir esta columna
      render: (value: string) => value?.toUpperCase() || "No disponible"
    },
    {
      key: 'DNI',
      title: 'DNI',
      className: 'col-dni',
      responsive: 'tablet' as const,
      expandable: false // No necesita expansión
    },
    {
      key: 'phoneNumber',
      title: 'Teléfono',
      className: 'col-phone',
      responsive: 'desktop' as const,
      expandable: false,
      render: (value: string) => PhoneNumber(value) || "No disponible"
    },
    {
      key: 'email',
      title: 'Email',
      className: 'col-email',
      expandable: false, // Útil para emails largos
      render: (value: string) => value?.toLowerCase() || "No disponible"
    },
    {
      key: 'fullAddress',
      title: 'Dirección',
      className: 'col-address',
      responsive: 'desktop' as const,
      expandable: false, // Muy útil para direcciones largas
      render: (value: string, row: any) =>
        value ? `${value}, ${row.supplementaryReference}` : "No disponible"
    },
    {
      key: 'isActive',
      title: 'Activo',
      className: 'col-status',
      expandable: false, // No necesita expansión
      render: (value: boolean) => value ? 'Sí' : 'No'
    },
    {
      key: 'actions',
      title: 'Acciones',
      className: 'col-actions',
      expandable: false, // Los botones no necesitan expansión
      render: (value: any, row: any) => (
        <div className="d-flex gap-1 justify-content-center">
          <TableActions
            type="View"
            onClick={() => openModal("ViewModal", row?.id, { chosenEmployee: row?.name, readOnly: true })}
          />
          <TableActions
            type="Edit"
            onClick={() => {
              setItems({ itemsPerPage: 20, page: 1 });
              openModal("EditModal", row?.id, { chosenEmployee: row?.name });
            }}
          />
          <TableActions
            type="Delete"
            onClick={() => {
              setItems({ itemsPerPage: 20, page: 1 });
              openModal("EliminateModal", row?.id, { chosenEmployee: row?.name });
            }}
          />
        </div>
      )
    }
  ];


  return (
    <div className="main-container employees-page">
      <div className="atendoo-card">
        {/* Header Section */}
        <div className="custom-header">
          <div className="header-top">
            <h6 className="subtitle">Empleados</h6>
            <InfoOffcanvas title="INFORMACIÓN DE TABLA">
              <ul>
                <li>La primera carga la tabla muestra los primeros 20 resultados.</li>
                <li>Se puede buscar con apellido, nombre o dni</li>
                <li>A medida que se va haciendo scroll para abajo, la tabla va buscando más resultados.</li>
              </ul>
            </InfoOffcanvas>
          </div>

          <div className="header-controls">
            {/* Search Section */}
            <div className="search-section">
              <div className="search-input-group">
                <input
                  title="Ingresar dni, apellido o nombre"
                  type="search"
                  placeholder="Buscar por apellido, nombre o DNI..."
                  className="search-input"
                  onChange={handleSearchChange}
                />
                <button
                  type="button"
                  className="search-btn"
                  onClick={() => {
                    setItems({
                      itemsPerPage: 20,
                      page: 1,
                    });
                    onSearch()
                  }}
                  title="Buscar"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Actions Section */}
            <div className="actions-section">
              {/* Results Count */}
              <div className="results-count">
                <span className="count-number">{data.length}</span>
                <span className="count-label">empleados</span>
              </div>

              {/* Download Button */}
              <button
                className="download-btn"
                onClick={getAllEmployees}
                title="Descargar reporte de todos los empleados"
                disabled={excelLoading}
              >
                {excelLoading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M8 10.667L8 2M8 10.667l-2.667-2.666M8 10.667l2.667-2.666"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <span className="btn-text">Excel</span>
              </button>

              {/* Add Employee Button */}
              {!isLoading && (
                <button
                  type="button"
                  className="add-btn"
                  onClick={() => openModal("AddModal")}
                  title="Agregar empleado"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 3.333v9.334M3.333 8h9.334"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="btn-text">Agregar</span>
                </button>
              )}

              {/* Import Button */}
              {!isLoading && (
                <button
                  className="import-btn"
                  onClick={() => openModal("ImportEmployeesModal")}
                  title="Importar empleados"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M5.333 6.667L8 4l2.667 2.667M8 4v8"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="btn-text">Importar</span>
                </button>
              )}

              {/* Loading Indicator */}
              {isLoading && (
                <div className="loading-indicator">
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                  </div>
                  <span>Cargando...</span>
                </div>
              )}
            </div>
          </div>
          <div><h5 className="text-center text-md text-red-500 m-2 p-2">Si desea pasar un empleado a postulante, comuníquese con el área comercial</h5></div>
        </div>

        {/* Table Section */}
        <InfiniteTable
          data={data}
          columns={employeeColumns}
          loading={isLoading}
          hasMore={hasMore}
          onLoadMore={getInfiniteEmployeesCall}
          height={470}
          emptyMessage="Momentáneamente no hay empleados cargados para mostrar"
          loadingMessage={data.length === 0 ? "Cargando empleados..." : "Buscando empleados..."}
          striped
          hover
          bordered
        />

        {/* Modals remain the same */}
        {isActive("AddModal") && (
          <AddEmployee
            refreshFunction={async () => await getEmployeesCallRefresh()}
            toggleModal
            urlTittle={"AddModal"}
          />
        )}

        {isActive("EliminateModal") && (
          <EliminarModal
            name={""}
            handleDeletion={HandleDeletion}
            urlTittle={"EliminateModal"}
            isLoading={isLoading}
            toggleModal
          />
        )}

        {isActive("EditModal") && (
          <EditEmployeeModal
            urlTittle={"EditModal"}
            onClose={() => closeModal("EditModal")}
            refreshFunction={async () => await getEmployeesCallRefresh()}
            toggleModal
            modalTitle='Editar empleado'
          />
        )}

        {isActive("ViewModal") && (
          <EditEmployeeModal
            urlTittle={"ViewModal"}
            onClose={() => closeModal("ViewModal")}
            refreshFunction={() => { }}
            toggleModal
            modalTitle='Ver empleado'
          />
        )}

        {isActive("ImportEmployeesModal") && (
          <ImportEmployeesModal
            show
            urlTittle={"ImportEmployeesModal"}
            onClose={() => closeModal("ImportEmployeesModal")}
            modalTitle={"Importar Empleados"}
          />
        )}
      </div>
    </div>
  );
};

export default Employees;
